import { DEFAULT_HISTORY_LEGEND_LABEL, DEFAULT_FUND_PARTNER_LABELS, DEFAULT_IMPORTANT_NOTES, SECTION_HEADER_DEFAULTS } from './PdfPreview';

// Template-specific mapping functions for template 68f53418b5f07bde00bc9e3b

export function mapV1FieldsToV2Data(fields: any) {
  const additional = fields?.additional_fields || {};
  const v2: any = {
    title: fields?.title || '',
    subtitle: additional?.subtitle || 'Factsheet Preview',
    published_at: fields?.published_at || fields?.wertentwicklung_seit_auflage_end_date || additional?.dateBadge || 'September 2025',
    data_as_of: additional?.data_as_of || additional?.dateBadge || fields?.wertentwicklung_seit_auflage_end_date,
    investmentApproach: additional?.investmentApproach,
    top10Stocks: additional?.top10Stocks,
    performance: additional?.performance,
    prices: fields?.preise || additional?.prices,
    conditions: fields?.konditionen || additional?.conditions,
    masterData: fields?.stammdaten || additional?.masterData,
    // Chart overrides (optional)
    historyIsin: additional?.historyIsin,
    historyUntil: additional?.historyUntil || fields?.wertentwicklung_seit_auflage_end_date,
    historyLegendLabel: additional?.historyLegendLabel || DEFAULT_HISTORY_LEGEND_LABEL,
    // Allocation (new)
    countryAllocation: additional?.countryAllocation,
    sectorAllocation: additional?.sectorAllocation,
    // Fund Partner and Contact (new)
    fundPartner: additional?.fundPartner,
    contact: additional?.contact,
    fundPartnerLabels: additional?.fundPartnerLabels || DEFAULT_FUND_PARTNER_LABELS,
    // Important Notes (new)
    importantNotes: additional?.importantNotes || DEFAULT_IMPORTANT_NOTES,
    numberFormatLocale: additional?.numberFormatLocale || 'DE',
    sectionHeaders: {
      ...SECTION_HEADER_DEFAULTS,
      ...(additional?.sectionHeaders || {}),
    },
  };
  return v2;
}

export function buildCustomizePayload(v2Data: any, original: any) {
  const payload: Array<{ key: string; value: any }> = [];
  const safeOriginal = original || {};
  const origAdditional = safeOriginal.additional_fields || {};

  if ((safeOriginal.title || '') !== (v2Data?.title || '')) {
    payload.push({ key: 'title', value: v2Data?.title || '' });
  }

  const nextPublished = v2Data?.published_at;
  if (nextPublished && safeOriginal.published_at !== nextPublished) {
    payload.push({ key: 'published_at', value: nextPublished });
  }

  if (v2Data?.prices && !deepEqual(safeOriginal.preise, v2Data.prices)) {
    payload.push({ key: 'preise', value: v2Data.prices });
  }
  if (v2Data?.conditions && !deepEqual(safeOriginal.konditionen, v2Data.conditions)) {
    payload.push({ key: 'konditionen', value: v2Data.conditions });
  }
  if (v2Data?.masterData && !deepEqual(safeOriginal.stammdaten, v2Data.masterData)) {
    payload.push({ key: 'stammdaten', value: v2Data.masterData });
  }

  const updatedAdditional: any = {};
  if (!deepEqual(origAdditional.subtitle, v2Data?.subtitle)) updatedAdditional.subtitle = v2Data?.subtitle;
  if (!deepEqual(origAdditional.dateBadge, v2Data?.published_at)) updatedAdditional.dateBadge = v2Data?.published_at;
  if (!deepEqual(origAdditional.investmentApproach, v2Data?.investmentApproach)) updatedAdditional.investmentApproach = v2Data?.investmentApproach;
  if (!deepEqual(origAdditional.top10Stocks, v2Data?.top10Stocks)) updatedAdditional.top10Stocks = v2Data?.top10Stocks;
  if (!deepEqual(origAdditional.performance, v2Data?.performance)) updatedAdditional.performance = v2Data?.performance;
  // Persist chart overrides in additional_fields
  if (!deepEqual(origAdditional.historyIsin, v2Data?.historyIsin)) updatedAdditional.historyIsin = v2Data?.historyIsin;
  if (!deepEqual(origAdditional.historyUntil, v2Data?.historyUntil)) updatedAdditional.historyUntil = v2Data?.historyUntil;
  if (!deepEqual(origAdditional.historyLegendLabel, v2Data?.historyLegendLabel)) updatedAdditional.historyLegendLabel = v2Data?.historyLegendLabel;
  if (!deepEqual(origAdditional.numberFormatLocale, v2Data?.numberFormatLocale)) {
    updatedAdditional.numberFormatLocale = v2Data?.numberFormatLocale || 'DE';
  }
  // Persist allocation
  if (!deepEqual(origAdditional.countryAllocation, v2Data?.countryAllocation)) updatedAdditional.countryAllocation = v2Data?.countryAllocation;
  if (!deepEqual(origAdditional.sectorAllocation, v2Data?.sectorAllocation)) updatedAdditional.sectorAllocation = v2Data?.sectorAllocation;
  // Persist fund partner and contact
  if (!deepEqual(origAdditional.fundPartner, v2Data?.fundPartner)) updatedAdditional.fundPartner = v2Data?.fundPartner;
  if (!deepEqual(origAdditional.contact, v2Data?.contact)) updatedAdditional.contact = v2Data?.contact;
  // Persist important notes
  if (!deepEqual(origAdditional.importantNotes, v2Data?.importantNotes)) updatedAdditional.importantNotes = v2Data?.importantNotes;
  const mergedOriginalPartnerLabels = {
    ...DEFAULT_FUND_PARTNER_LABELS,
    ...(origAdditional.fundPartnerLabels || {}),
  };
  const mergedCurrentPartnerLabels = {
    ...DEFAULT_FUND_PARTNER_LABELS,
    ...(v2Data?.fundPartnerLabels || {}),
  };
  if (!deepEqual(mergedOriginalPartnerLabels, mergedCurrentPartnerLabels)) {
    updatedAdditional.fundPartnerLabels = mergedCurrentPartnerLabels;
  }
  const mergedOriginalHeaders = {
    ...SECTION_HEADER_DEFAULTS,
    ...(origAdditional.sectionHeaders || {}),
  };
  const mergedCurrentHeaders = {
    ...SECTION_HEADER_DEFAULTS,
    ...(v2Data?.sectionHeaders || {}),
  };
  if (!deepEqual(mergedOriginalHeaders, mergedCurrentHeaders)) {
    updatedAdditional.sectionHeaders = mergedCurrentHeaders;
  }

  // Also update core V1 end-date field if changed to keep backend parity
  if (v2Data?.historyUntil && !deepEqual(safeOriginal.wertentwicklung_seit_auflage_end_date, v2Data?.historyUntil)) {
    payload.push({ key: 'wertentwicklung_seit_auflage_end_date', value: v2Data?.historyUntil });
  }

  if (Object.keys(updatedAdditional).length) {
    const merged = { ...origAdditional, ...updatedAdditional };
    payload.push({ key: 'additional_fields', value: merged });
  }

  return payload;
}

function deepEqual(a: any, b: any) {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return a === b;
  }
}

